import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Paper, Divider, IconButton, Card, CardContent, Tooltip, Zoom } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SchoolIcon from '@mui/icons-material/School';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ScienceIcon from '@mui/icons-material/Science';

const LanguageLearningBenefits: React.FC = () => {
  // State for interactive elements
  const [expandedBenefit, setExpandedBenefit] = useState<number | null>(null);
  const [hoveredResearch, setHoveredResearch] = useState<number | null>(null);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const benefits = [
    {
      title: "Контекстуальное обучение",
      description: "Исследования показывают, что изучение слов в контексте повышает запоминаемость на 40% по сравнению с изолированным заучиванием. Фильмы обеспечивают богатый эмоциональный и ситуативный контекст.",
      statistic: "40%",
      detail: "улучшение запоминания",
      icon: <AutoStoriesIcon fontSize="large" />,
      color: "#6a3de8",
      expanded: "Контекст создает нейронные связи между словами и ситуациями, что делает запоминание более естественным. Когда мы видим слово в реальной ситуации с эмоциональным контекстом, наш мозг создает более прочные ассоциации."
    },
    {
      title: "Естественная разговорная речь",
      description: "Традиционные учебники часто содержат устаревшие или формальные выражения. Фильмы демонстрируют современный, живой язык в том виде, в котором он действительно используется носителями.",
      statistic: "78%",
      detail: "диалогов содержат разговорные выражения",
      icon: <VisibilityIcon fontSize="large" />,
      color: "#e85a0d",
      expanded: "Разговорные выражения, сленг и идиомы редко встречаются в учебниках, но составляют значительную часть повседневного общения. Фильмы и сериалы позволяют слышать язык таким, каким он используется в реальной жизни."
    },
    {
      title: "Культурное погружение",
      description: "Язык неотделим от культуры. Фильмы передают нюансы культурного контекста, идиомы и невербальную коммуникацию, что невозможно получить из стандартных учебников.",
      statistic: "5x",
      detail: "больше культурных отсылок, чем в учебниках",
      icon: <SchoolIcon fontSize="large" />,
      color: "#0d93e8",
      expanded: "Жесты, мимика, интонации и культурные отсылки составляют до 70% коммуникации. Фильмы позволяют наблюдать эти элементы в естественной среде, значительно обогащая языковые навыки."
    },
    {
      title: "Эмоциональная вовлеченность",
      description: "Нейробиологические исследования подтверждают, что эмоционально окрашенная информация лучше запоминается. Увлекательные сцены из фильмов создают эмоциональные якоря для языкового материала.",
      statistic: "2.5x",
      detail: "выше запоминаемость эмоционально окрашенной лексики",
      icon: <PsychologyIcon fontSize="large" />,
      color: "#e83d8c",
      expanded: "Эмоции активируют миндалевидное тело мозга, что усиливает консолидацию памяти. Когда мы эмоционально вовлечены в сюжет, наш мозг выделяет больше ресурсов для запоминания языковых конструкций."
    }
  ];
  
  const researchPoints = [
    {
      text: "Исследование Массачусетского технологического института показало, что изучение языка через медиаконтент ускоряет освоение разговорной речи в 3 раза.",
      icon: <ScienceIcon />,
      highlight: "в 3 раза быстрее"
    },
    {
      text: "Согласно данным Гарвардского университета, аудиовизуальное обучение активирует на 30% больше областей мозга, чем традиционные методы обучения.",
      icon: <PsychologyIcon />,
      highlight: "на 30% больше активности мозга"
    },
    {
      text: "Исследования в области нейролингвистики показывают, что восприятие языка в мультисенсорном контексте (видео+аудио) формирует более стабильные нейронные связи.",
      icon: <EmojiObjectsIcon />,
      highlight: "стабильные нейронные связи"
    }
  ];

  // Floating particles animation effect
  const particles = Array(12).fill(0).map((_, i) => ({
    id: i,
    size: Math.random() * 10 + 5,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));

  return (
    <Box component="section" sx={{ py: 8, position: 'relative', overflow: 'hidden' }}>
      {/* Floating animated particles */}
      {particles.map(particle => (
        <Box
          key={particle.id}
          component={motion.div}
          sx={{
            position: 'absolute',
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            background: `rgba(79, 109, 245, ${Math.random() * 0.2 + 0.05})`,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            zIndex: 0
          }}
          animate={{
            x: ['-20px', '20px', '-20px'],
            y: ['-30px', '30px', '-30px'],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: particle.duration,
            ease: 'easeInOut',
            repeat: Infinity,
            delay: particle.delay
          }}
        />
      ))}
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box textAlign="center" mb={6}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2 className="section-title pencil-text">Почему это работает</h2>
            <p className="section-description pencil-text">
              Научно обоснованный подход к изучению языка через кинематографический опыт
            </p>
          </motion.div>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Grid container spacing={4} mb={6}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div 
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.03,
                    transition: { duration: 0.2 }
                  }}
                >
                  <Card 
                    elevation={3} 
                    sx={{ 
                      height: '100%',
                      borderRadius: 4,
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: expandedBenefit === index ? `2px solid ${benefit.color}` : 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '5px',
                        background: benefit.color
                      }
                    }}
                    onClick={() => setExpandedBenefit(expandedBenefit === index ? null : index)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: benefit.color }}>
                          {benefit.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h5" component="h3" fontWeight={600} mb={0.5}>
                            {benefit.title}
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            ml: 2, 
                            p: 1.5, 
                            borderRadius: 2,
                            bgcolor: `${benefit.color}22`,
                            color: benefit.color,
                            textAlign: 'center',
                            minWidth: '80px'
                          }}
                        >
                          <Typography variant="h4" fontWeight={700}>
                            {benefit.statistic}
                          </Typography>
                          <Typography variant="caption" fontWeight={500} display="block">
                            {benefit.detail}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body1" color="text.secondary">
                        {benefit.description}
                      </Typography>
                      
                      {/* Expanded content */}
                      {expandedBenefit === index && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.3 }}
                        >
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" sx={{ p: 1, bgcolor: `${benefit.color}11`, borderRadius: 2 }}>
                            {benefit.expanded}
                          </Typography>
                        </motion.div>
                      )}
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mt: 2,
                        color: 'text.secondary',
                        fontSize: '12px'
                      }}>
                        {expandedBenefit === index ? 'Нажмите, чтобы свернуть' : 'Нажмите для подробностей'}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Card 
            elevation={3}
            sx={{ 
              p: 4, 
              borderRadius: 4,
              background: 'linear-gradient(135deg, #f8faff 0%, #e8efff 100%)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(79,109,245,0.1) 0%, rgba(255,255,255,0) 70%)',
                borderRadius: '0 0 0 100%',
                zIndex: 0
              }}
            />
            
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '120px',
                height: '120px',
                background: 'radial-gradient(circle, rgba(255,107,153,0.1) 0%, rgba(255,255,255,0) 70%)',
                borderRadius: '0 100% 0 0',
                zIndex: 0
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h5" 
                component="h3" 
                fontWeight={600} 
                mb={3} 
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'primary.dark'
                }}
              >
                <ScienceIcon sx={{ mr: 1 }} /> Что говорит наука
              </Typography>
              
              <Grid container spacing={3}>
                {researchPoints.map((point, index) => (
                  <Grid item xs={12} key={index}>
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      onMouseEnter={() => setHoveredResearch(index)}
                      onMouseLeave={() => setHoveredResearch(null)}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          p: 2, 
                          borderRadius: 2,
                          bgcolor: hoveredResearch === index ? 'rgba(79, 109, 245, 0.08)' : 'transparent',
                          transition: 'all 0.2s ease',
                          border: hoveredResearch === index ? '1px solid rgba(79, 109, 245, 0.2)' : '1px solid transparent'
                        }}
                      >
                        <Box 
                          sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            bgcolor: 'rgba(79, 109, 245, 0.1)',
                            p: 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {point.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">
                            {point.text}
                          </Typography>
                          {hoveredResearch === index && (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  mt: 1, 
                                  color: 'primary.main',
                                  fontWeight: 600,
                                  display: 'inline-block',
                                  bgcolor: 'rgba(79, 109, 245, 0.1)',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1
                                }}
                              >
                                {point.highlight}
                              </Typography>
                            </motion.div>
                          )}
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="body1" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                Словари, созданные на основе фильмов, обеспечивают уникальный мост между формальным изучением языка и 
                реальной языковой практикой. Они помогают преодолеть культурный разрыв и значительно сокращают время, 
                необходимое для достижения разговорной беглости.
              </Typography>
            </Box>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LanguageLearningBenefits; 